import { ENTITIES, getCustomSortFieldConfigs } from '../../../constants/customSortFieldConfigs';
import { GROUP_BY_FIELD_NAMES, GROUP_BY_FIELDS, SORT_WORKSPACE_NAME } from './groupByConstants';

type GroupByType = {
    fieldName: (typeof GROUP_BY_FIELDS)[keyof typeof GROUP_BY_FIELDS];
    label: string;
    sortId?: string;
};

type ReportSortFormConfig = {
    sortByFieldName: string;
    sortByLabelToken: string;
    sortByKeys: GroupByType[];
    groupByFieldName: string;
    groupByLabelToken: string;
    groupByHelperTextToken?: string;
    resolveGroupByKeys: (entity: (typeof ENTITIES)[keyof typeof ENTITIES]) => GroupByType[];
    cohortSortByFieldName: string;
    cohortSortByLabelToken: string;
    cohortSortByKeys: GroupByType[];
};

export const rankSortByKeys: GroupByType[] = [
    { fieldName: GROUP_BY_FIELDS.rankAsc, label: 'form.field.sort.rank.asc' },
    { fieldName: GROUP_BY_FIELDS.rankDesc, label: 'form.field.sort.rank.desc' }
];

export const publicTitleSortByKeys: GroupByType[] = [
    { fieldName: GROUP_BY_FIELDS.publicTitleAsc, label: 'form.field.sort.name.asc' },
    { fieldName: GROUP_BY_FIELDS.publicTitleDesc, label: 'form.field.sort.name.desc' }
];

export const nameSortByKeys: GroupByType[] = [
    { fieldName: GROUP_BY_FIELDS.nameAsc, label: 'form.field.sort.name.asc' },
    { fieldName: GROUP_BY_FIELDS.nameDesc, label: 'form.field.sort.name.desc' }
];

export const commonSortByKeys: GroupByType[] = [
    { fieldName: GROUP_BY_FIELDS.createdAsc, label: 'form.field.sort.created.asc' },
    { fieldName: GROUP_BY_FIELDS.createdDesc, label: 'form.field.sort.created.desc' },
    { fieldName: GROUP_BY_FIELDS.updatedAsc, label: 'form.field.sort.updated.asc' },
    { fieldName: GROUP_BY_FIELDS.updatedDesc, label: 'form.field.sort.updated.desc' }
];

export const customSortByKey: GroupByType[] = [{ fieldName: GROUP_BY_FIELDS.custom, label: 'common.custom' }];

export const workspaceSortByKeys: GroupByType[] = [
    ...publicTitleSortByKeys,
    ...rankSortByKeys,
    ...commonSortByKeys,
    ...customSortByKey
];

export const submissionSortByKeys: GroupByType[] = [...publicTitleSortByKeys, ...commonSortByKeys, ...customSortByKey];

export const shortlistSortByKeys: GroupByType[] = [...nameSortByKeys, ...commonSortByKeys, ...customSortByKey];

export const cohortSortByKeys: GroupByType[] = [...publicTitleSortByKeys, ...rankSortByKeys];

export const keyPeopleGroupByKeys: GroupByType[] = [
    { fieldName: GROUP_BY_FIELDS.keyPeopleName, label: 'form.name' },
    { fieldName: GROUP_BY_FIELDS.keyPeopleRole, label: 'common.role' }
];
export const keyOrganizationGroupByKeys: GroupByType[] = [
    { fieldName: GROUP_BY_FIELDS.keyOrganizationName, label: 'form.name' },
    { fieldName: GROUP_BY_FIELDS.keyOrganizationRole, label: 'common.role' }
];
export const projectOwnersGroupByKeys: GroupByType[] = [
    { fieldName: GROUP_BY_FIELDS.projectOwnersName, label: 'form.name' },
    { fieldName: GROUP_BY_FIELDS.projectOwnersRole, label: 'common.role' }
];

export const getGroupByKeysForEntity = (
    entity: (typeof ENTITIES)[keyof typeof ENTITIES] = ENTITIES.workspace
): GroupByType[] => {
    return getCustomSortFieldConfigs(entity).map((config) => ({
        fieldName: config.groupId as GroupByType['fieldName'],
        label: config.groupByLabelToken,
        ...(config.sortId ? { sortId: config.sortId } : {})
    }));
};

export const workspaceGroupByKeys: GroupByType[] = getGroupByKeysForEntity();

const REPORT_SORT_FORM_CONFIGS_BY_ENTITY: Record<(typeof ENTITIES)[keyof typeof ENTITIES], ReportSortFormConfig> = {
    [ENTITIES.workspace]: {
        sortByFieldName: SORT_WORKSPACE_NAME.sortWorkspacesBy,
        sortByLabelToken: 'form.field.sort.by',
        sortByKeys: workspaceSortByKeys,
        groupByFieldName: GROUP_BY_FIELD_NAMES.workspaceGroupBy,
        groupByLabelToken: 'form.field.group.by',
        groupByHelperTextToken: 'report.field.group.description',
        resolveGroupByKeys: getGroupByKeysForEntity,
        cohortSortByFieldName: SORT_WORKSPACE_NAME.sortCohortsBy,
        cohortSortByLabelToken: 'form.field.sort.cohorts.by',
        cohortSortByKeys: cohortSortByKeys
    },
    [ENTITIES.submission]: {
        sortByFieldName: SORT_WORKSPACE_NAME.sortWorkspacesBy,
        sortByLabelToken: 'form.field.sort.by',
        sortByKeys: submissionSortByKeys,
        groupByFieldName: GROUP_BY_FIELD_NAMES.workspaceGroupBy,
        groupByLabelToken: 'form.field.group.by',
        groupByHelperTextToken: 'report.field.group.description',
        resolveGroupByKeys: getGroupByKeysForEntity,
        cohortSortByFieldName: SORT_WORKSPACE_NAME.sortCohortsBy,
        cohortSortByLabelToken: 'form.field.sort.cohorts.by',
        cohortSortByKeys: [...publicTitleSortByKeys, ...commonSortByKeys]
    },
    [ENTITIES.shortlist]: {
        sortByFieldName: SORT_WORKSPACE_NAME.sortWorkspacesBy,
        sortByLabelToken: 'form.field.sort.by',
        sortByKeys: shortlistSortByKeys,
        groupByFieldName: GROUP_BY_FIELD_NAMES.workspaceGroupBy,
        groupByLabelToken: 'form.field.group.by',
        groupByHelperTextToken: 'report.field.group.description',
        resolveGroupByKeys: getGroupByKeysForEntity,
        cohortSortByFieldName: SORT_WORKSPACE_NAME.sortCohortsBy,
        cohortSortByLabelToken: 'form.field.sort.cohorts.by',
        cohortSortByKeys: nameSortByKeys
    }
};

export const getReportSortFormConfig = (entity: (typeof ENTITIES)[keyof typeof ENTITIES]): ReportSortFormConfig => {
    return REPORT_SORT_FORM_CONFIGS_BY_ENTITY[entity] || REPORT_SORT_FORM_CONFIGS_BY_ENTITY[ENTITIES.workspace];
};

/**
 * Creates an array of options (id, label, sortId) by mapping over a key list
 * and translating the label using the provided i18n function.
 * @param keys The array of key items (e.g., keyPeopleGroupByKeys).
 * @param i18nFunction The internationalization function (useI18n).
 * @returns An array of option objects { id: string, label: string, sortId?: string }.
 */
export const createGroupByOptions = (keys: GroupByType[], i18nFunction: (token: string) => string) => {
    return keys.map((item) => ({
        id: item.fieldName,
        label: i18nFunction(item.label),
        ...(item.sortId ? { sortId: item.sortId } : { sortId: item.fieldName })
    }));
};
